import { Alert, Checkbox, Chip, InputAdornment, Tooltip } from "@mui/material";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbCategory } from "react-icons/tb";

import { CommonLabel } from "@/components/CommonInput";
import CommonSelector from "@/components/CommonSelector";
import DriverAvatar from "@/components/DriverAvatar";
import { User } from "@/types/user.types";

import { CreateNotificationFormType } from "./CreateNotificationForm";

function CreateNotificationRecipientUserSelector({ users }: { users: User[] }) {
    const { setValue } = useFormContext();
    const selectedItems: string[] = useWatch({ name: "recipients.userIds" as unknown as readonly string[] }) || [];
    const { t } = useTranslation();
    const selectedUsers = useMemo(
        () => users.filter((item) => selectedItems.includes(item.id)),
        [users, selectedItems]
    );

    const handleDelete = (id: string) => {
        setValue(
            "recipients.userIds",
            selectedItems.filter((item) => item !== id)
        );
    };

    const handleChange = (value: (User | null)[] | NonNullable<User> | null) => {
        const userIds =
            value && Array.isArray(value)
                ? value.map((item) => item?.id).filter((id): id is string => Boolean(id))
                : [];
        setValue("recipients.userIds", userIds);
    };

    return (
        <>
            <div className="col-span-full">
                <Alert severity="info" color="info">
                    Bildirim göndermek istediğiniz kullanıcıları seçiniz.
                </Alert>
            </div>
            <span className="text-gray-500 col-span-full text-sm pl-2">
                {selectedItems.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 justify-start">
                        {selectedUsers.map((user) => {
                            return (
                                user && (
                                    <Chip
                                        key={user.id}
                                        label={
                                            <DriverAvatar
                                                avatar
                                                userType={user.userType}
                                                isDeleted={user.disabled}
                                                name={user.name}
                                            />
                                        }
                                        variant="outlined"
                                        sx={{
                                            maxWidth: "fit-content",
                                            borderColor: "transparent !important",
                                        }}
                                        onDelete={() => handleDelete(user.id)}
                                    />
                                )
                            );
                        })}
                    </div>
                ) : (
                    "Lütfen En az 1 kullanıcı seçiniz."
                )}
            </span>

            <CommonLabel className="col-span-full" label={t("users")}>
                <CommonSelector<CreateNotificationFormType, User>
                    icon={TbCategory}
                    name="recipients.userIds"
                    options={users}
                    getOptionLabel={(val) => {
                        return val ? val.name : "";
                    }}
                    value={selectedUsers}
                    disableCloseOnSelect
                    placeholder="user"
                    multiple
                    onChange={handleChange}
                    startAdornment={
                        <Tooltip title="Seçilen kişi sayısı">
                            <InputAdornment
                                position="end"
                                className="!-translate-x-[6px] cursor-help !font-semibold !-translate-y-[12px] !w-12 !h-full !flex !items-center !bg-gray-100 !justify-center"
                            >
                                {selectedItems.length}
                            </InputAdornment>
                        </Tooltip>
                    }
                    renderOption={({ option, selected }) => {
                        return (
                            option && (
                                <>
                                    <div className="flex justify-between items-center w-full ">
                                        <Checkbox
                                            className=" mr-2"
                                            color="primary"
                                            size="small"
                                            checked={!!selected}
                                            value={option}
                                            onChange={() => void 0}
                                        />
                                        <DriverAvatar
                                            avatar
                                            userType={option.userType}
                                            isDeleted={option.disabled}
                                            name={option.name}
                                        />
                                    </div>
                                </>
                            )
                        );
                    }}
                />
            </CommonLabel>
        </>
    );
}

export default CreateNotificationRecipientUserSelector;
